import { CircularProgress } from '@material-ui/core';
import { UserPlusIcon } from '@phosphor-icons/react';
import { useAlert } from '@orientaction/hooks';
import { adminService } from '@orientaction/services';
import { getFromLS } from '@orientaction/utils';
import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { fetchAssignableStaffUsers } from '../../shared/assignableStaff';
import { BENEFICIARY_ROLE_ID } from '../../shared/constants';
import { ConsultantSummary } from '../../shared/types';
import { readRole, sortAssignableStaff } from '../../shared/utils';
import {
  REASSIGN_HIERARCHY_EMPTY,
  REASSIGN_HIERARCHY_MISMATCH,
  REASSIGN_HIERARCHY_SUCCESS,
  buildReassignHierarchyLabel,
  buildReassignHierarchyTitle,
} from '../constants';
import { useStyles } from '../styles';
import ReassignBeneficiariesModal from './ReassignBeneficiariesModal';

interface HierarchyUser {
  id: number;
  firstname?: string;
  lastname?: string;
  email?: string;
  username?: string;
  company_user_role?: any;
}

interface HierarchyUsersTabProps {
  users: HierarchyUser[];
  attachedRoleId: number;
  attachedLabel: string;
  user: any;
  roleLabel: string;
  canReassign?: boolean;
  loading?: boolean;
  emptyLabel?: string;
  onReassigned?: () => void;
}

const HierarchyUsersTab: FC<HierarchyUsersTabProps> = ({
  users,
  attachedRoleId,
  attachedLabel,
  user,
  roleLabel,
  canReassign = false,
  loading = false,
  emptyLabel = 'Aucun utilisateur rattaché',
  onReassigned,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const alert = useAlert();
  const token = getFromLS('userToken') || '';
  const userConnected = JSON.parse(getFromLS('userInfos') || '{}');
  const currentUserId = user?.id;
  const currentRoleId = readRole(user?.company_user_role)?.id;
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignLoading, setReassignLoading] = useState(false);
  const [reassignSaving, setReassignSaving] = useState(false);
  const [eligibleUsers, setEligibleUsers] = useState<ConsultantSummary[]>([]);

  const goToProfile = (item: HierarchyUser) => {
    const itemRole = readRole(item.company_user_role)?.id || attachedRoleId;
    if (itemRole === BENEFICIARY_ROLE_ID) {
      history.push(`/admin/users-resultats/beneficiaire/${item.id}`);
      return;
    }
    history.push(`/admin/users-resultats/consultant/${item.id}`);
  };

  const closeReassignModal = () => {
    if (reassignSaving) {
      return;
    }
    setReassignOpen(false);
    setEligibleUsers([]);
  };

  const handleReassign = async () => {
    const companyId = Number(
      user?.company?.id || user?.company?.data?.id || userConnected?.company?.id
    );
    if (!currentUserId || !companyId || !currentRoleId) {
      alert.openSimpleAlert();
      return;
    }

    setReassignOpen(true);
    setReassignLoading(true);
    try {
      const staff = await fetchAssignableStaffUsers({
        companyId,
        consultantOnly: false,
        roleId: currentRoleId,
      });
      setEligibleUsers(
        sortAssignableStaff(
          staff
            .filter((item: any) => Number(item.id) !== Number(currentUserId))
            .map((item: any): ConsultantSummary => {
              const role = readRole(item.company_user_role);
              return {
                id: Number(item.id),
                firstname: item.firstname || '',
                lastname: item.lastname || '',
                company_user_role: role || undefined,
              };
            })
        )
      );
    } catch {
      setEligibleUsers([]);
      alert.openSimpleAlert();
    } finally {
      setReassignLoading(false);
    }
  };

  const saveReassign = async (nextUserId: number) => {
    if (!currentUserId || !nextUserId || reassignSaving) {
      return;
    }
    setReassignSaving(true);
    try {
      await adminService.reassigned(
        {
          id_for_assigned: currentUserId,
          id_current_assigned: nextUserId,
        },
        token
      );
      setReassignOpen(false);
      setEligibleUsers([]);
      alert.setOpenAlert(true, REASSIGN_HIERARCHY_SUCCESS, 'success');
      onReassigned?.();
    } catch (err: any) {
      if (err?.response?.data?.error?.details?.id === 'transfer_failed') {
        alert.setOpenAlert(true, REASSIGN_HIERARCHY_MISMATCH, 'error');
      } else {
        alert.openSimpleAlert();
      }
    } finally {
      setReassignSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={classes.loader}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <div className={classes.tableWrap}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th className={classes.thLeft}>Utilisateurs</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td className={classes.emptyCell}>{emptyLabel}</td>
              </tr>
            ) : (
              users.map(item => {
                const fullName =
                  `${item.firstname || ''} ${item.lastname || ''}`.trim() ||
                  item.email ||
                  item.username ||
                  '—';
                return (
                  <tr key={item.id}>
                    <td>
                      <div className={classes.beneficiaryCell}>
                        <button
                          type="button"
                          className={classes.beneficiaryName}
                          onClick={() => goToProfile(item)}>
                          {fullName}
                        </button>
                        {item.email ? (
                          <span className={classes.beneficiaryEmail}>{item.email}</span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {canReassign ? (
        <div className={classes.tableFooter}>
          <button
            type="button"
            className={classes.reassignBtn}
            onClick={handleReassign}
            disabled={users.length === 0 || reassignSaving}>
            <UserPlusIcon size={18} />
            {buildReassignHierarchyTitle(attachedLabel)}
          </button>
        </div>
      ) : null}

      <ReassignBeneficiariesModal
        open={reassignOpen}
        loading={reassignLoading}
        saving={reassignSaving}
        consultants={eligibleUsers}
        title={buildReassignHierarchyTitle(attachedLabel)}
        fieldLabel={buildReassignHierarchyLabel(roleLabel)}
        hint=""
        emptyText={REASSIGN_HIERARCHY_EMPTY}
        onClose={closeReassignModal}
        onSave={saveReassign}
      />
    </>
  );
};

export default HierarchyUsersTab;
